import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Modal, Spinner, Badge } from 'react-bootstrap';
import { getDatabase, ref, onValue, update } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AlertMessage from '../../AlertMessage';
import { FaWhatsapp, FaCheckCircle, FaEdit } from 'react-icons/fa';

const WhatsAppManagement = () => {
  const udiseNumber = localStorage.getItem('udiseNumber');
  const schoolName = localStorage.getItem('selectedStudentName') || "School Dashboard"; 
  
  const [config, setConfig] = useState({
    enabled: false,
    attendanceWebhook: '',
    feesWebhook: '',
    attendanceApiEnabled: false,
    feesApiEnabled: false,
  });

  const [initialConfig, setInitialConfig] = useState({
    attendanceWebhookSaved: false,
    feesWebhookSaved: false,
  });

  const [editMode, setEditMode] = useState({
    attendanceWebhook: true,
    feesWebhook: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  // Test WhatsApp State
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (alertMessage) {
      setShowAlert(true);
      const timeoutId = setTimeout(() => {
        setShowAlert(false);
        setAlertMessage('');
      }, 4000);
      return () => clearTimeout(timeoutId);
    }
  }, [alertMessage]);

  useEffect(() => {
    if (!udiseNumber) {
      setLoading(false);
      return;
    }

    const db = getDatabase();
    const configRef = ref(db, `schoolRegister/${udiseNumber}/whatsappConfig`);
    
    const unsubscribe = onValue(configRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        setConfig(prev => ({
          ...prev,
          enabled: !!data.enabled,
          attendanceWebhook: data.attendanceWebhook || '',
          feesWebhook: data.feesWebhook || '',
          attendanceApiEnabled: !!data.attendanceApiEnabled,
          feesApiEnabled: !!data.feesApiEnabled
        }));

        setInitialConfig({
          attendanceWebhookSaved: !!data.attendanceWebhook,
          feesWebhookSaved: !!data.feesWebhook,
        });

        setEditMode({
          attendanceWebhook: !data.attendanceWebhook,
          feesWebhook: !data.feesWebhook,
        });
      }
      setLoading(false);
    }, (error) => {
      console.error("Error loading WhatsApp config:", error);
      setAlertMessage("Failed to load WhatsApp configuration.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [udiseNumber]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig({
      ...config,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const toggleEditMode = (field) => {
    setEditMode(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!udiseNumber) {
      setAlertMessage("No active school selected.");
      return;
    }

    setSaving(true);
    const db = getDatabase();
    const configRef = ref(db, `schoolRegister/${udiseNumber}/whatsappConfig`);

    try {
      const payload = {
        enabled: config.enabled,
        provider: 'whatapi',
        attendanceWebhook: config.attendanceWebhook,
        feesWebhook: config.feesWebhook,
        attendanceApiEnabled: config.attendanceApiEnabled,
        feesApiEnabled: config.feesApiEnabled
      };

      await update(configRef, payload);
      
      setAlertMessage("WhatsApp configuration saved successfully.");
      
      setInitialConfig({
        attendanceWebhookSaved: !!config.attendanceWebhook,
        feesWebhookSaved: !!config.feesWebhook,
      });

      setEditMode({
        attendanceWebhook: !config.attendanceWebhook,
        feesWebhook: !config.feesWebhook,
      });
      
    } catch (error) {
      console.error("Error saving WhatsApp config:", error);
      setAlertMessage(error.message || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleTestWhatsApp = async (e) => {
    e.preventDefault();
    if (!testPhoneNumber) {
      setTestResult({ success: false, message: "Please enter a valid mobile number." });
      return;
    }

    if (!config.attendanceWebhook && !config.feesWebhook) {
      setTestResult({ success: false, message: "Please configure and save a webhook URL first before testing." });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      let webhookUrl = config.attendanceWebhook || config.feesWebhook;
      
      webhookUrl = webhookUrl.replace(/number=[^&]+/, `number=${testPhoneNumber}`);
                            
      webhookUrl = webhookUrl.replace('studentname', 'Test Student')
                             .replace('attendancestatus', 'Present')
                             .replace('date', new Date().toLocaleDateString('en-GB'))
                             .replace('class', 'X')
                             .replace('division', 'A')
                             .replace('schoolname', 'VedGurukul')
                             .replace('remark', 'Test Message')
                             .replace('contactnumber', testPhoneNumber);

      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors' 
      });
      
      setTestResult({ success: true, message: "Test request sent successfully! Please check the WhatsApp number." });
      
    } catch (error) {
      console.error("Error testing WhatsApp:", error);
      setTestResult({ success: false, message: "Failed to connect to the Webhook URL. Please verify your connection." });
    } finally {
      setTesting(false);
    }
  };

  if (!udiseNumber) {
    return (
      <Container className="p-5 text-center mt-5">
        <h4>Please select a school to manage WhatsApp settings.</h4>
      </Container>
    );
  }

  return (
    <div className="main-content-of-page">
      <AlertMessage message={alertMessage} show={showAlert} />
      
      <Container fluid className="p-4 mt-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow-sm border-0" style={{ borderRadius: '15px' }}>
              <Card.Header 
                className="text-white text-center py-3" 
                style={{ backgroundColor: '#004d99', borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}
              >
                <h4 className="mb-0"><FaWhatsapp className="me-2" /> WhatsApp Management</h4>
              </Card.Header>
              
              <Card.Body className="p-4">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <Form onSubmit={handleSave}>
                    <div className="mb-4 text-center">
                      <p className="text-muted mb-1">UDISE Number: <strong>{udiseNumber}</strong></p>
                      <p className="mb-0">
                        Status: {' '}
                        {config.enabled ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-secondary">Disabled</span>
                        )}
                      </p>
                    </div>

                    <Form.Group className="mb-4 border-bottom pb-3">
                      <Form.Check 
                        type="switch"
                        id="whatsapp-enabled-switch"
                        label="Enable WhatsApp Integration for this School"
                        name="enabled"
                        checked={config.enabled}
                        onChange={handleChange}
                        className="fw-bold text-primary"
                        style={{ fontSize: '1.1rem' }}
                      />
                    </Form.Group>

                    <h5 className="mb-3">Attendance Configuration</h5>
                    <Card className="mb-4 bg-light border-0">
                      <Card.Body>
                        <Form.Group className="mb-3">
                          <Form.Check 
                            type="switch"
                            id="attendance-api-enabled-switch"
                            label="Enable Attendance WhatsApp Messages"
                            name="attendanceApiEnabled"
                            checked={config.attendanceApiEnabled}
                            onChange={handleChange}
                            className="fw-bold mb-2"
                          />
                        </Form.Group>

                        {config.attendanceApiEnabled && (
                          <Form.Group>
                            <Form.Label className="fw-semibold">Attendance Webhook URL</Form.Label>
                            {editMode.attendanceWebhook ? (
                              <Form.Control 
                                type="text" 
                                name="attendanceWebhook"
                                value={config.attendanceWebhook}
                                onChange={handleChange}
                                placeholder="https://webhook.whatapi.in/webhook/...&message=..."
                                required={config.attendanceApiEnabled}
                              />
                            ) : (
                              <div className="d-flex align-items-center p-2 border rounded bg-white">
                                <FaCheckCircle className="text-success me-2" />
                                <span className="flex-grow-1 text-success fw-bold">Uploaded successfully</span>
                                <Button variant="outline-primary" size="sm" onClick={() => toggleEditMode('attendanceWebhook')}>
                                  <FaEdit className="me-1" /> Update
                                </Button>
                              </div>
                            )}
                            {editMode.attendanceWebhook && (
                              <Form.Text className="text-muted">Enter the full webhook URL with template variables.</Form.Text>
                            )}
                          </Form.Group>
                        )}
                      </Card.Body>
                    </Card>

                    <h5 className="mb-3">Fees Configuration</h5>
                    <Card className="mb-4 bg-light border-0">
                      <Card.Body>
                        <Form.Group className="mb-3">
                          <Form.Check 
                            type="switch"
                            id="fees-api-enabled-switch"
                            label="Enable Fees Collection WhatsApp Messages"
                            name="feesApiEnabled"
                            checked={config.feesApiEnabled}
                            onChange={handleChange}
                            className="fw-bold mb-2"
                          />
                        </Form.Group>

                        {config.feesApiEnabled && (
                          <Form.Group>
                            <Form.Label className="fw-semibold">Fees Webhook URL</Form.Label>
                            {editMode.feesWebhook ? (
                              <Form.Control 
                                type="text" 
                                name="feesWebhook"
                                value={config.feesWebhook}
                                onChange={handleChange}
                                placeholder="https://webhook.whatapi.in/webhook/...&message=..."
                                required={config.feesApiEnabled}
                              />
                            ) : (
                              <div className="d-flex align-items-center p-2 border rounded bg-white">
                                <FaCheckCircle className="text-success me-2" />
                                <span className="flex-grow-1 text-success fw-bold">Uploaded successfully</span>
                                <Button variant="outline-primary" size="sm" onClick={() => toggleEditMode('feesWebhook')}>
                                  <FaEdit className="me-1" /> Update
                                </Button>
                              </div>
                            )}
                            {editMode.feesWebhook && (
                              <Form.Text className="text-muted">Enter the full webhook URL with template variables.</Form.Text>
                            )}
                          </Form.Group>
                        )}
                      </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-between mt-4 border-top pt-3">
                      <Button 
                        variant="success" 
                        type="submit" 
                        disabled={saving}
                        className="px-4"
                        size="lg"
                      >
                        {saving ? <Spinner size="sm" className="me-2"/> : null}
                        Save Configuration
                      </Button>

                      <Button 
                        variant="outline-primary" 
                        onClick={() => {
                          setTestResult(null);
                          setShowTestModal(true);
                        }}
                        disabled={!config.enabled}
                      >
                        Test WhatsApp
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Test WhatsApp Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa' }}>
          <Modal.Title><FaWhatsapp className="me-2 text-success" /> Test WhatsApp</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleTestWhatsApp}>
            <Form.Group className="mb-3">
              <Form.Label>Recipient Mobile Number</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="e.g. 919876543210" 
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                required
              />
              <Form.Text className="text-muted">Include country code without '+'.</Form.Text>
            </Form.Group>

            {testResult && (
              <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
                {testResult.message}
              </div>
            )}

            <div className="d-grid mt-4">
              <Button variant="primary" type="submit" disabled={testing}>
                {testing ? <Spinner size="sm" className="me-2"/> : "Test Send"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WhatsAppManagement;
